import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ResponsiveNavigation from '../../components/ResponsiveNavigation';
import { ArrowLeft, Save, Building2 } from 'lucide-react';

const FIELDS = [
  { key: 'name',         label: 'Company name', required: true },
  { key: 'email',        label: 'Email' },
  { key: 'phone',        label: 'Phone' },
  { key: 'website',      label: 'Website' },
  { key: 'city',         label: 'City' },
  { key: 'country',      label: 'Country' },
  { key: 'industry',     label: 'Industry' },
  { key: 'company_size', label: 'Company size' },
];

const AdminCompanyEdit = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${backendBase}/api/admin/companies/${companyId}/detail`);
      const d = r.data?.data || {};
      const next = {};
      FIELDS.forEach((f) => { next[f.key] = d[f.key] || ''; });
      next.status = d.status || 'active';
      setForm(next);
    } catch (e) { setError('Failed to load company.'); }
    finally { setLoading(false); }
  }, [backendBase, companyId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError('');
    if (!form.name?.trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    try {
      await axios.put(`${backendBase}/api/admin/companies/${companyId}`, form);
      navigate(`/admin/companies/${companyId}`);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ResponsiveNavigation />
        <div className="p-8 text-center text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-company-edit-page">
      <ResponsiveNavigation />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate(`/admin/companies/${companyId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
          data-testid="company-edit-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Company detail
        </button>

        <div className="bg-white border border-gray-200 rounded-2xl">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Edit company</h1>
              <p className="text-xs text-gray-500 mt-0.5">Update the company information shown across the app.</p>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.key} className={f.key === 'name' || f.key === 'website' ? 'sm:col-span-2' : ''}>
                <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <input
                  type="text"
                  value={form[f.key] || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  data-testid={`company-edit-field-${f.key}`}
                />
              </div>
            ))}
            <div>
              <label className="block text-[11px] font-semibold text-gray-700 mb-1.5">Status</label>
              <select
                value={form.status || 'active'}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="company-edit-field-status"
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {error && <p className="px-6 -mt-2 mb-2 text-xs text-red-600" data-testid="company-edit-error">{error}</p>}

          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
            <button onClick={() => navigate(`/admin/companies/${companyId}`)} className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900" data-testid="company-edit-cancel">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg"
              data-testid="company-edit-save"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCompanyEdit;
